<script setup lang="ts">
/**
 * A chart, drawn: the chart section's, or one placed freely on a page.
 *
 * Fills whatever box it is put in; the one placing it decides its size.
 *
 * **The canvas is registered for export** under the id of whatever owns it.
 * A `<canvas>` serialises through `outerHTML` with no bitmap at all, so a
 * chart would print blank; the export asks each one for a picture of itself
 * instead (see `features/charts/registry.ts`).
 *
 * Chart.js is registered here rather than globally, and only the pieces these
 * four chart types need: registering everything pulls in scales and
 * controllers a financial report never draws.
 */
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PieController,
  PointElement,
  Tooltip,
} from 'chart.js'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { chartHouse, type ChartContent } from '@/domain/chart'
import type { Layout } from '@/domain/layout'
import { typefaceOf, type TypefaceId } from '@/domain/typeface'
import { chartConfiguration } from '@/features/charts/drawing'
import { registerChartCanvas, unregisterChartCanvas } from '@/features/charts/registry'
import { useReportStore } from '@/stores/report'

Chart.register(
  BarController,
  LineController,
  PieController,
  DoughnutController,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip,
)

const props = defineProps<{
  chart: ChartContent
  /** Whose chart it is: the section's id, or the free element's. What the export finds it by. */
  owner: string
  /**
   * A look to draw it in other than the report's: a house style being tried
   * on, before it is applied.
   */
  styled?: { readonly layout: Layout; readonly typeface: TypefaceId }
}>()

const report = useReportStore()

/** The look the chart is drawn in: the one being tried on, or the report's. */
function look(): { layout: Layout; typeface: TypefaceId } {
  return props.styled ?? { layout: report.layout, typeface: report.typeface }
}

const canvas = ref<HTMLCanvasElement | null>(null)
let drawn: Chart | null = null

function draw(): void {
  const element = canvas.value
  if (!element) return
  drawn?.destroy()
  drawn = new Chart(
    element,
    chartConfiguration(props.chart, {
      accent: look().layout.accent,
      house: chartHouse(look().layout),
      font: typefaceOf(look().typeface).stack,
    }),
  )
}

/** Every word and figure the chart sets, so the faces holding their letters can be asked for. */
function words(): string {
  const { labels, series, centre, unit } = props.chart
  return [...labels, ...series.map((one) => one.label), centre, unit, '0123456789,'].join(' ')
}

/**
 * Drawn at once, and again once the faces its letters need have loaded.
 *
 * A typeface served in pieces (Latin, accented Latin, and so on) loads each
 * piece when a page first uses it, and a canvas does not wait: whatever is not
 * loaded yet is drawn in a stand-in and stays so, since a canvas is a picture
 * and never redraws itself.
 */
let asked = 0
function drawWithFonts(): void {
  draw()
  const fonts = typeof document === 'undefined' ? undefined : document.fonts
  if (!fonts) return
  const stack = typefaceOf(look().typeface).stack
  const text = words()
  const ask = ++asked
  void Promise.all(
    ['400', '600', '700'].map((weight) => fonts.load(`${weight} 11px ${stack}`, text)),
  )
    .then((loaded) => {
      if (ask === asked && loaded.some((faces) => faces.length > 0)) draw()
    })
    .catch(() => undefined)
}

onMounted(() => {
  drawWithFonts()
  if (canvas.value) registerChartCanvas(props.owner, canvas.value)
})

// Redrawn rather than updated in place: the shape of the data can change
// entirely between edits, and `chart.update()` is only cheaper when it cannot.
watch(
  () => {
    const { layout, typeface } = look()
    return [props.chart, layout.accent, chartHouse(layout), typeface]
  },
  () => drawWithFonts(),
  { deep: true },
)

onBeforeUnmount(() => {
  unregisterChartCanvas(props.owner)
  drawn?.destroy()
  drawn = null
})
</script>

<template>
  <div class="chart-canvas">
    <canvas ref="canvas" :data-chart-id="owner" />
  </div>
</template>

<style scoped>
/* Chart.js sizes the canvas to this box, which must be its own. */
.chart-canvas {
  position: relative;
  width: 100%;
  height: 100%;
}
</style>
